import { Injectable } from '@angular/core';

interface PwaChoiceResult {
  outcome: 'accepted' | 'dismissed';
  platform: string;
}

export interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<PwaChoiceResult>;
}

@Injectable({
  providedIn: 'root'
})
export class PwaInstallService {
  private deferredPrompt?: BeforeInstallPromptEvent;

  capturePrompt(event: Event): void {
    if (this.isRunningStandalone()) {
      this.clearPrompt();
      return;
    }

    this.deferredPrompt = event as BeforeInstallPromptEvent;
  }

  clearPrompt(): void {
    this.deferredPrompt = undefined;
  }

  shouldShowInstall(): boolean {
    return !this.isRunningStandalone() && !!this.deferredPrompt;
  }

  canPromptInstall(): boolean {
    return !!this.deferredPrompt;
  }

  async install(): Promise<void> {
    if (!this.deferredPrompt) {
      return;
    }

    const deferredPrompt = this.deferredPrompt;
    await deferredPrompt.prompt();

    try {
      await deferredPrompt.userChoice;
    } finally {
      this.clearPrompt();
    }
  }

  isRunningStandalone(): boolean {
    if (typeof window === 'undefined') {
      return false;
    }

    return window.matchMedia('(display-mode: standalone)').matches
      || (window.navigator as Navigator & { standalone?: boolean }).standalone === true;
  }
}
